/**
 * Singleton confirm-dialog state.
 *
 * `<ConfirmDialog />` is mounted once at App.vue. Anywhere in the app:
 *
 *   const ok = await confirm({ title: "Cancel booking?", body: "...", danger: true });
 *   if (ok) await store.cancel(id);
 *
 * Only one dialog is open at a time. A second `confirm()` call while one
 * is already open will replace the previous prompt's resolver — keep the
 * UX simple and don't queue.
 */
import { ref } from "vue";

export interface ConfirmOptions {
  title: string;
  body?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Renders the confirm button red and the icon as a warning. */
  danger?: boolean;
}

interface ActiveConfirm extends ConfirmOptions {
  resolve: (ok: boolean) => void;
}

const active = ref<ActiveConfirm | null>(null);

function confirm(opts: ConfirmOptions): Promise<boolean> {
  // If another dialog is already open, resolve it as cancelled before
  // showing the new one so we never leave a hanging promise.
  if (active.value) {
    active.value.resolve(false);
  }
  return new Promise<boolean>((resolve) => {
    active.value = { ...opts, resolve };
  });
}

function _resolveActive(ok: boolean) {
  if (active.value) {
    active.value.resolve(ok);
    active.value = null;
  }
}

export function useConfirm() {
  return {
    active,
    confirm,
    accept: () => _resolveActive(true),
    reject: () => _resolveActive(false),
  };
}
